import type { LiteralUnion } from 'type-fest';

import { getDemoEnvironment } from './demoEnvironment';
import { detectEnvironment } from './detectEnvironment';
import type { BuildEnvironment, DemoEnvironment, Environment, SubEnvironment } from './types';

export type Options = Readonly<{
	/**
	 * hostname will be used to determine the environment (if not directly specified)
	 */
	hostname?: string;
	/**
	 * if available (usually server-side), this can be specified instead of hostname
	 */
	environment?: Environment;
	/**
	 * if on a demo environment, this should be its name (e.g. jade, cyan, or personalized demo environments like jlou that points to cyan)
	 */
	demoEnvironment?: LiteralUnion<DemoEnvironment, string>;
	/**
	 * if on a personalized demo environment, this should be its target environment (e.g. jade, cyan)
	 */
	demoEnvironmentTarget?: DemoEnvironment;
	/**
	 * can be used to override the environment
	 *
	 * This can be useful in dev env where the app is proxying to a different environment, e.g. demo or staging
	 *
	 * defaults to what environment ends up being
	 */
	apiEnvironment?: Environment;
	/**
	 * can be used to override the demoEnvironment
	 *
	 * This can be useful in dev env where the app is proxying to a different environment, e.g. demo or staging
	 *
	 * defaults to what demoEnvironment ends up being
	 */
	apiDemoEnvironment?: LiteralUnion<DemoEnvironment, string> | undefined;
	/**
	 * can be used to override the demoEnvironmentTarget
	 *
	 * This can be useful in dev env where the app is proxying to a different environment, e.g. demo or staging
	 *
	 * defaults to what demoEnvironmentTarget ends up being
	 */
	apiDemoEnvironmentTarget?: DemoEnvironment | undefined;
	buildEnvironment: BuildEnvironment;
}>;

/* eslint-disable @typescript-eslint/consistent-type-definitions, @typescript-eslint/method-signature-style */
interface EnvironmentUtils {
	getEnvironment(): Environment;
	getDemoEnvironment(): LiteralUnion<DemoEnvironment, string> | undefined;
	getSubEnvironment(): LiteralUnion<SubEnvironment, string>;
	getDemoEnvironmentTarget(): DemoEnvironment | undefined;
	getApiEnvironment(): Environment;
	getApiDemoEnvironment(): LiteralUnion<DemoEnvironment, string> | undefined;
	getApiSubEnvironment(): LiteralUnion<SubEnvironment, string>;
	getApiDemoEnvironmentTarget(): DemoEnvironment | undefined;
	getBuildEnvironment(): BuildEnvironment;
}
/* eslint-enable @typescript-eslint/consistent-type-definitions, @typescript-eslint/method-signature-style */

class EnvironmentUtilsImpl implements EnvironmentUtils {
	private readonly hostname?: string;
	private readonly environment: Environment;
	private readonly demoEnvironment: LiteralUnion<DemoEnvironment, string> | undefined;
	private readonly demoEnvironmentTarget: DemoEnvironment | undefined;
	private readonly apiEnvironment: Environment;
	private readonly apiDemoEnvironment: LiteralUnion<DemoEnvironment, string> | undefined;
	private readonly apiDemoEnvironmentTarget: DemoEnvironment | undefined;
	private readonly buildEnvironment: BuildEnvironment;

	constructor({
		hostname,
		buildEnvironment,
		demoEnvironment,
		demoEnvironmentTarget,
		environment,
		apiEnvironment,
		apiDemoEnvironment,
		apiDemoEnvironmentTarget,
	}: Options) {
		if (!hostname && !environment) {
			throw new Error('Please specify at least hostname or environment');
		}
		this.hostname = hostname;
		this.buildEnvironment = buildEnvironment;
		this.demoEnvironment = demoEnvironment || (this.hostname && getDemoEnvironment(this.hostname));
		this.demoEnvironmentTarget = demoEnvironmentTarget;
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		this.environment = environment || detectEnvironment(hostname!, this.buildEnvironment);
		this.apiEnvironment = apiEnvironment || this.environment;
		this.apiDemoEnvironment = apiDemoEnvironment || this.demoEnvironment;
		this.apiDemoEnvironmentTarget = apiDemoEnvironmentTarget || this.demoEnvironmentTarget;
	}

	/**
	 * The environment the app is running on
	 */
	getEnvironment(): Environment {
		return this.environment;
	}

	/**
	 * The demo sub-environment the app is running on
	 */
	getDemoEnvironment(): string | undefined {
		return this.demoEnvironment;
	}

	/**
	 * The demo sub-environment target the app is running on (e.g. if the demo environment is a personalized demo environment like jlou that points to cyan, then this would return cyan)
	 */
	getDemoEnvironmentTarget(): DemoEnvironment | undefined {
		return this.demoEnvironmentTarget;
	}

	/**
	 * The sub-environment the app is running on (e.g. production, staging, jade, teal, noir, plum, vert, cyan, opal, orion, or personalized demo environments like jlou that points to cyan)
	 */
	getSubEnvironment(): LiteralUnion<SubEnvironment, string> {
		return this.demoEnvironment ? this.demoEnvironment : this.environment;
	}

	/**
	 * The environment of the APIs (or third party services) the app is targeting
	 */
	getApiEnvironment(): Environment {
		return this.apiEnvironment;
	}

	/**
	 * The demo sub-environment of the APIs (or third party services) the app is targeting
	 */
	getApiDemoEnvironment(): string | undefined {
		return this.apiDemoEnvironment;
	}

	/**
	 * The demo sub-environment target of the APIs (or third party services) the app is targeting
	 */
	getApiDemoEnvironmentTarget(): DemoEnvironment | undefined {
		return this.apiDemoEnvironmentTarget;
	}

	/**
	 * The sub-environment of the APIs (or third party services) the app is targeting
	 *
	 * e.g. production, staging, jade, teal, noir, plum, vert, cyan, opal, orion, or personalized demo environments (e.g. jlou that points to cyan)
	 */
	getApiSubEnvironment(): LiteralUnion<SubEnvironment, string> {
		return this.apiDemoEnvironment ? this.apiDemoEnvironment : this.apiEnvironment;
	}

	/**
	 * The build environment the app was built for
	 */
	getBuildEnvironment(): BuildEnvironment {
		return this.buildEnvironment;
	}
}

export type { EnvironmentUtils };

export function createEnvironmentUtils(options: Options): EnvironmentUtils {
	return new EnvironmentUtilsImpl(options);
}

export function createEnvironmentUtilsFake(errorMsg: string): EnvironmentUtils {
	const errorFn = () => {
		throw new Error(errorMsg);
	};
	return {
		getBuildEnvironment: errorFn,
		getDemoEnvironment: errorFn,
		getDemoEnvironmentTarget: errorFn,
		getEnvironment: errorFn,
		getSubEnvironment: errorFn,
		getApiDemoEnvironment: errorFn,
		getApiEnvironment: errorFn,
		getApiSubEnvironment: errorFn,
		getApiDemoEnvironmentTarget: errorFn,
	};
}
